import * as React from 'react';
import * as _ from 'lodash';
import { ITransformHeader, ITransformRow } from '../../../../models/ITransform';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import cancelEvent from '../../../../services/mini/cancelEvent';
import { Loader, LoaderSize, LoaderTheme } from '../../../components/Loader/Loader';
import { FillTransformResultButton } from './FillTransformResultButton/FillTransformResultButton';
import { formatValueForTransform } from './formatValueForTransform';
import { HIGLIGHT_CELL_RULES } from './transformTextHighlight';
import addTabSpacesTotextarea from '../../../../services/mini/addTabSpacesTotextarea';
import { getValidationMsgForJSONFromTransform, isTransformValueValidJSON } from './cleanupTransformsJSON';
import { isSpellCheckAvailable } from './transformSpellCheck';
import ToasterService from '../../../../services/ToasterService';
import copyJSONToBuffer from '../../../../services/mini/copyJSONToBuffer';
import { useIsMounted } from '../../../../hooks/useIsMounted';
import { observer } from 'mobx-react';
import { transformsStore } from '../../../../mobx/transforms.store';
import setCaretAtTheBeginning from '../../../../services/mini/setCaretAtTheBegining';
import { Environment } from '../../../../const/environment';
import { CountTransformTokens } from './CountTransformTokens/CountTransformTokens';



type TransformCellProps = {
    row: ITransformRow,
    name: string
    transformName: string
    prefix?: string
    placeholder: string
    editable: boolean
    previewMode: boolean
    hasChanges: boolean
    isPopupShown: boolean
    onUpdate: (name: string, value: string) => void;
    onFormatUpdate: (name: string, value: string) => void;
    onEnterEditMode: () => void;
    onClosePopup: () => void;
    onOpenPopup: (name: string,) => void;
    header: ITransformHeader,
    index: number,
    environment: Environment
}
export const TransformCell = observer(({
    row,
    name,
    index,
    prefix,
    placeholder,
    onUpdate,
    header,
    hasChanges,
    onOpenPopup,
    editable,
    isPopupShown,
    environment
}: TransformCellProps) => {
    const value = _.isObject(row[name]) ? JSON.stringify(row[name]) : row[name];
    const containerRef = useRef();
    const textareaRef = useRef<HTMLTextAreaElement>();
    const isMounted = useIsMounted();
    const [isAllTextSelected, setIsAllTextSelected] = useState(false);
    const [hovered, setHovered] = useState(false);

    const handleRevertChangesForResult = useCallback((e) => {
        if (name === 'result') {
            if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
                if (transformsStore.hasValueToRevertResultBeforeGeneration(row.id)) {
                    cancelEvent(e);
                    transformsStore.revertResultBeforeGeneration(row.id);
                    setTimeout(() => { updateHighlighting(); });
                }
            }
        }
    }, [row.id, name]);

    const handleGenerationFinish = useCallback(() => {
        setTimeout(() => {
            setCaretAtTheBeginning(textareaRef.current);
        }, 1200);
    }, []);

    const openEditPopupCb = useCallback(() => {
        onOpenPopup(name);
    }, [name, onOpenPopup]);

    const applyHighlighting = useCallback(() => {
        try {
            window.$('.textarea-with-highlighted-text', containerRef?.current).highlightWithinTextarea({
                highlight: HIGLIGHT_CELL_RULES
            });
        } catch (error) {
            console.info('err', error);
        }
    }, []);

    const updateHighlighting = useCallback(() => {
        try {
            window.$('.textarea-with-highlighted-text', containerRef?.current).highlightWithinTextarea('update');
        } catch (error) {
            console.info('err', error);
        }
    }, []);

    useEffect(() => {
        let timeoutId;
        if (row?.isResultLoading) return;
        if (!row?.isNew) {
            timeoutId = setTimeout(() => {
                if (isMounted && !row?.isResultLoading) {
                    applyHighlighting();
                }
            }, 1000);
        } else {
            applyHighlighting();
        }
        return () => {
            clearTimeout(timeoutId);
        };
    }, [applyHighlighting, isMounted, isPopupShown, row?.isNew, row?.isResultLoading]);

    useLayoutEffect(() => {
        if (row.isResultLoading && name === 'result' && textareaRef?.current) {
            textareaRef.current.scrollTop = textareaRef?.current.scrollHeight;
        }
    }, [row.result]);

    useEffect(() => {
        if (row.deleting) {
            window.$('.textarea-with-highlighted-text', containerRef?.current).highlightWithinTextarea('destroy');
        }
    }, [row.deleting]);

    const handleTextareaKeydown = useCallback((e) => {
        addTabSpacesTotextarea(e);
        updateHighlighting();
        handleRevertChangesForResult(e);
    }, [name, onUpdate, updateHighlighting, handleRevertChangesForResult]);

    const format = header[name]?.format || 'text';
    const isValid = isTransformValueValidJSON(format, value);
    const jsonValidationMsg = getValidationMsgForJSONFromTransform(format, value);
    const isLoading = row.isResultLoading && name === 'result';
    const isEmpty = !row[name];

    const textVal = formatValueForTransform(value);
    const textValMaxLength = 250;

    const handleCopy = useCallback((e) => {
        if (isAllTextSelected) {
            copyJSONToBuffer(textVal);
            cancelEvent(e)
        }
    }, [textVal, copyJSONToBuffer, isAllTextSelected]);

    const handleSelect = useCallback(e => {
        const textarea = e.target;
        const text = textarea.value;
        const selectionStart = textarea.selectionStart;
        const selectionEnd = textarea.selectionEnd;

        if (selectionStart === 0 && selectionEnd === text.length) {
            setIsAllTextSelected(true);
        } else {
            setIsAllTextSelected(false);
        }
    }, []);

    const timerRef = useRef(null);

    const handleMouseEnter = () => {
        setHovered(true);
    };

    const handleMouseLeave = () => {
        clearTimeout(timerRef.current);
        setHovered(false);
    };

    return <div className={`transform-view__cell ${prefix && 'with-prefix'} ${editable ? 'edit-mode' : 'view-mode'} ${isEmpty && 'transform-cell-empty'} ${hasChanges && editable ? 'has-changes' : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={containerRef}>
        <textarea
            ref={textareaRef}
            className="textarea-with-highlighted-text"
            placeholder={placeholder}
            spellCheck={isSpellCheckAvailable(format)}
            value={(editable || textVal?.length < textValMaxLength) ? textVal : textVal?.substr(0, textValMaxLength) + '... (open row to see more)'}
            readOnly={!editable}
            onChange={(e) => {
                onUpdate(name, e.target.value);
            }}
            onSelect={handleSelect}
            onCopy={handleCopy}
            onKeyDown={handleTextareaKeydown}
        ></textarea>
        <div
            title="Open row preview"
            className={`transform-view__popup-btn ${(name === 'result' && editable) ? 'not-last' : ''} `} onClick={(e) => {
                openEditPopupCb();
                cancelEvent(e);
            }}>
            <i className="icon icon-alan-search" />
        </div>

        {(editable && name === 'result') &&
            <FillTransformResultButton
                row={row}
                index={index}
                environment={environment}
                onFinish={handleGenerationFinish}
            />}

        <div
            className={`transform-view__copy-btn ${(isLoading || !value) && 'disabled'}`}
            title="Copy value"
            onClick={(e) => {
                copyJSONToBuffer(textVal);
                ToasterService.showInfo({ title: `Copied!` });
                cancelEvent(e);
            }}>
            <i className="icon icon-alan-test-copy" />
        </div>

        {(!isValid) && <i title={jsonValidationMsg} className="icon transform-view__err-icon icon-alan-info-sign" />}

        {isLoading && <Loader size={LoaderSize.Small} theme={LoaderTheme.Dark} center={true} />}

        {hovered && <CountTransformTokens
            debounceDelay={0}
            maxTokensVisible={0}
            value={row[name]} />}

    </div>;
});